/**
 * Timesheet Settings API Client
 * Handles API calls for organization timesheet settings
 */

import { apiClient, ApiError } from '@/services/apiClient';
import type { OrganizationTimesheetSettingsDto } from '@/types/api';
import { 
  fromOrganizationTimesheetSettingsDto,
  toOrganizationTimesheetSettingsDto 
} from './mappers';
import type { OrganizationTimesheetSettings } from '@/types/timesheetSettings';

/**
 * Get organization timesheet settings from API
 * @returns Organization settings with RowVersion for concurrency control
 */
export async function getOrganizationSettings(): Promise<OrganizationTimesheetSettings & { rowVersion: string }> {
  const dto = await apiClient.get<OrganizationTimesheetSettingsDto>('/settings/timesheet');
  return fromOrganizationTimesheetSettingsDto(dto);
}

/**
 * Update organization timesheet settings via API
 * @param settings - The settings to update
 * @param rowVersion - Current RowVersion for optimistic concurrency control
 * @returns Updated settings with new RowVersion
 * @throws ApiError with status 409 if concurrency conflict occurs
 */
export async function updateOrganizationSettings(
  settings: OrganizationTimesheetSettings,
  rowVersion: string
): Promise<OrganizationTimesheetSettings & { rowVersion: string }> {
  const dto = toOrganizationTimesheetSettingsDto(settings, rowVersion);
  
  try {
    const updatedDto = await apiClient.put<OrganizationTimesheetSettingsDto>('/settings/timesheet', dto);
    return fromOrganizationTimesheetSettingsDto(updatedDto);
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      throw new ApiError(
        409,
        'Conflict',
        'Settings have been modified by another user. Please refresh and try again.'
      );
    }
    throw error;
  }
}
